<template>
  <VcModal
    class="add-or-update-address-modal"
    :title="title"
    max-width="60rem"
    hide-actions
    is-mobile-fullscreen
    test-id="edit-address-modal"
  >
    <template #default="{ close }">
      <AddressForm
        :model-value="editableAddress"
        :countries="countries"
        :disabled="loading"
        :server-errors="serverErrors"
        with-personal-info
        required-email
        required-city
        data-test-id="address-form"
        @save="saveAddress"
      >
        <template #append="{ dirty, valid }">
          <div class="add-or-update-address-modal__actions">
            <VcButton min-width="8rem" color="secondary" variant="outline" data-test-id="cancel-button" @click="close">
              {{ $t("common.buttons.cancel") }}
            </VcButton>

            <VcButton
              min-width="8rem"
              :disabled="!dirty || !valid"
              :loading="loading"
              data-test-id="submit-button"
              type="submit"
            >
              {{ saveButtonLabel }}
            </VcButton>
          </div>
        </template>
      </AddressForm>
    </template>
  </VcModal>
</template>

<script setup lang="ts">
import { clone } from "lodash-es";
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useCountries } from "@/core/composables";
import { AddressForm } from "@/shared/common";
import type { MemberAddressType } from "@/core/api/graphql/types";

interface IEmits {
  (event: "result", address: MemberAddressType): void;
}

interface IProps {
  address?: MemberAddressType;
  loading?: boolean;
  serverErrors?: Partial<Record<keyof MemberAddressType, string>>;
}

const emit = defineEmits<IEmits>();

const props = defineProps<IProps>();

const { countries, loadCountries } = useCountries();
const { t } = useI18n();

const editableAddress = ref<MemberAddressType>();

const title = computed<string>(() =>
  editableAddress.value ? t("common.titles.edit_address") : t("common.titles.new_address"),
);
const saveButtonLabel = computed(() => (editableAddress.value ? t("common.buttons.save") : t("common.buttons.create")));

onMounted(async () => {
  if (!countries.value.length) {
    await loadCountries();
  }
});

function saveAddress(address: MemberAddressType) {
  emit("result", address);
}

watchEffect(() => {
  editableAddress.value = clone(props.address);
});
</script>

<style lang="scss">
.add-or-update-address-modal {
  &__actions {
    @apply flex flex-wrap items-center justify-between gap-4 pt-2;

    @media (width < theme("screens.xs")) {
      & > * {
        @apply flex-1;
      }
    }
  }
}
</style>
